import React, { useState, useEffect } from 'react';

import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import useSmartBack from '@hooks/shared/useSmartBack';
import PageHeader from '@shared/PageHeader';
import { Button, Form, Input, InputNumber, Row, Col, Spin, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// 材積係數：與後端 app/models/consumable.rb 對齊
// cuft（材）= 長×寬×高(cm) ÷ 27000；cbm（立方公尺）= ÷ 1,000,000
const VOLUME_DIVISORS = { cuft: 27000, cbm: 1000000 };
const DEFAULT_VOLUME_UNIT = 'cuft'; // 系統預設（Organization.volume_unit DB default = CUFT）

/**
 * Consumable Edit/New Form (Common Component)
 *
 * @param {Object} props
 * @param {Function} props.loadConsumable - Function to load consumable data: (id) => Promise<response>
 * @param {Function} props.createConsumable - Function to create consumable: (data) => Promise<response>
 * @param {Function} props.updateConsumable - Function to update consumable: (id, data) => Promise<response>
 * @param {string} props.volumeUnit - Org volume unit ('cbm'|'cuft') from role context; decides 材積 divisor + 顯示單位
 * @param {string} props.consumableId - Consumable ID (if editing)
 * @param {string} props.role - Role (admin or operator)
 */
export default function ConsumableEdit({
  loadConsumable,
  createConsumable,
  updateConsumable,
  volumeUnit,
  consumableId,
  role = 'admin'
}) {
  const { t } = useTranslation();
  const goBack = useSmartBack();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // 區分「儲存並新增下一筆」的 loading，避免兩顆按鈕同時轉 spinner
  const [savingAndAdding, setSavingAndAdding] = useState(false);
  const isEditMode = !!consumableId;

  // 機構體積單位（cbm / cuft），決定材積係數與顯示單位；取不到時 fallback cuft（系統預設）
  const normalizedVolumeUnit = (volumeUnit && VOLUME_DIVISORS[String(volumeUnit).toLowerCase()])
    ? String(volumeUnit).toLowerCase()
    : DEFAULT_VOLUME_UNIT;
  const volumeDivisor = VOLUME_DIVISORS[normalizedVolumeUnit];

  // 監聽長寬高變化，自動計算材積（唯讀預覽，實際值由後端依機構單位計算）
  const length = Form.useWatch('length', form);
  const width = Form.useWatch('width', form);
  const height = Form.useWatch('height', form);
  useEffect(() => {
    // 當長寬高都有值時，自動計算 volume
    if (length && width && height) {
      const calculatedVolume = (length * width * height) / volumeDivisor;
      form.setFieldValue('volume', parseFloat(calculatedVolume.toFixed(5)));
    }
  }, [length, width, height, volumeDivisor, form]);
  useEffect(() => {
    if (isEditMode) {
      loadData();
    }
  }, [consumableId]);
  const loadData = async () => {
    setLoading(true);
    try {
      const response = await loadConsumable(consumableId);
      if (response.success) {
        form.setFieldsValue(response.data);
      } else {
        message.error(response.message || t('common.error.load_failed'));
      }
    } catch (error) {
      console.error('Failed to load consumable:', error);
      message.error(t('common.error.load_failed'));
    } finally {
      setLoading(false);
    }
  };
  // 核心提交邏輯：呼叫 create/update 並回傳 response 給呼叫者決定後續行為
  const submitValues = async (values) => {
    return isEditMode
      ? await updateConsumable(consumableId, values)
      : await createConsumable(values);
  };

  // 將 response.errors 套回欄位（保留原有錯誤處理邏輯）
  const applyFieldErrors = (response) => {
    if (response.errors) {
      const fieldErrors = {};
      Object.keys(response.errors).forEach(key => {
        fieldErrors[key] = {
          errors: Array.isArray(response.errors[key])
            ? response.errors[key]
            : [response.errors[key]]
        };
      });
      form.setFields(Object.keys(fieldErrors).map(name => ({
        name,
        errors: fieldErrors[name].errors
      })));
    }
  };

  // 主要提交：成功後導向 show 頁（新建與編輯皆適用）
  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const response = await submitValues(values);
      if (response.success) {
        message.success(t(isEditMode ? 'common.message.update_success' : 'common.message.create_success'));
        // 導向該筆記錄的 show 頁，避免返回快取的列表頁看不到新資料
        const recordId = response.data?.id || consumableId;
        navigate(`/consumables/${recordId}`, { replace: true });
      } else {
        applyFieldErrors(response);
        message.error(response.message || t('common.error.save_failed'));
      }
    } catch (error) {
      console.error('Failed to save consumable:', error);
      message.error(t('common.error.save_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  // 儲存並新增下一筆：僅在新建模式可用，成功後重置表單留在本頁
  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      setSavingAndAdding(true);
      try {
        const response = await submitValues(values);
        if (response.success) {
          message.success(t('common.message.create_success'));
          form.resetFields();
        } else {
          applyFieldErrors(response);
          message.error(response.message || t('common.error.save_failed'));
        }
      } catch (error) {
        console.error('Failed to save consumable:', error);
        message.error(t('common.error.save_failed'));
      } finally {
        setSavingAndAdding(false);
      }
    } catch {
      // validateFields 失敗會 reject，antd 會自動高亮錯誤欄位
    }
  };
  const basePath = role === 'operator' ? '/operator' : '/admin';
  const breadcrumbItems = role === 'operator'
    ? ['warehouse_settings', 'warehouse_settings.consumables', { label: isEditMode ? t('common.button.edit') : t('common.button.create') }]
    : ['data', 'data.consumables', { label: isEditMode ? t('common.button.edit') : t('common.button.create') }];

  return (
    <div>
      <PageHeader
        basePath={basePath}
        items={breadcrumbItems}
      />
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ maxWidth: 800 }}
        >
          <Form.Item
            label={t(`models.consumable.name`)}
            name="name"
            rules={[{ required: true, message: t('common.validation.required') }]}
          >
            <Input placeholder={t(`common.placeholder.please_enter`)} />
          </Form.Item>
          <Form.Item
            label={t(`models.consumable.barcode`)}
            name="barcode"
            rules={[{ required: true, message: t('common.validation.required') }]}
          >
            <Input placeholder={t(`common.placeholder.please_enter`)} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                label={t(`models.consumable.length`)}
                name="length"
                rules={[{ type: 'number', min: 0 }]}
              >
                <InputNumber
                  placeholder={t(`common.placeholder.please_enter`)}
                  style={{ width: '100%' }}
                  min={0}
                  addonAfter="cm"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label={t(`models.consumable.width`)}
                name="width"
                rules={[{ type: 'number', min: 0 }]}
              >
                <InputNumber
                  placeholder={t(`common.placeholder.please_enter`)}
                  style={{ width: '100%' }}
                  min={0}
                  addonAfter="cm"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label={t(`models.consumable.height`)}
                name="height"
                rules={[{ type: 'number', min: 0 }]}
              >
                <InputNumber
                  placeholder={t(`common.placeholder.please_enter`)}
                  style={{ width: '100%' }}
                  min={0}
                  addonAfter="cm"
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label={t(`models.consumable.volume`)}
            name="volume"
          >
            {/* 材積為長寬高換算的唯讀計算值，單位依機構體積單位設定；後端不收此值、會自行重算 */}
            <InputNumber
              placeholder={t(`common.placeholder.please_enter`)}
              style={{ width: '100%' }}
              min={0}
              step={0.00001}
              disabled
              addonAfter={t(`enums.organization.volume_unit.${normalizedVolumeUnit}`)}
            />
          </Form.Item>
          <FloatingActionBarSpacer />
        </Form>
      </Spin>

      <FloatingActionBar
        role={role}
        onCancel={goBack}
        onSubmit={() => form.submit()}
        loading={submitting}
        disabled={savingAndAdding}
        extraButtons={!isEditMode ? (
          <Button
            loading={savingAndAdding}
            disabled={submitting}
            onClick={handleSaveAndAddAnother}
          >
            {t('common.button.save_and_add_another')}
          </Button>
        ) : null}
      />
    </div>
  );
}
